import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { FormField, Switch } from '../../components/FormField.jsx';
import { PageHeader } from '../../components/PageHeader.jsx';
import { Modal, ConfirmDialog, submitForm } from '../../components/Modal.jsx';
import { EmptyState } from './Teams.jsx';

const empty = {
  email: '', firstName: '', lastName: '', phone: '',
  marketingOptIn: true, source: 'manual', listIds: [], notes: '',
};

const sourceChip = {
  signup: 'chip-success',
  join_request: 'chip-brand',
  clubhouse_inquiry: 'chip-warn',
  manual: 'chip-neutral',
  import: 'chip-neutral',
};

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [lists, setLists] = useState([]);
  const [filters, setFilters] = useState({ listId: '', optIn: '', source: '', suppression: 'active' });
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  async function syncFromRecords() {
    setSyncing(true); setSyncMsg(null);
    try {
      const r = await Admin.contacts.sync();
      setSyncMsg(`Imported ${r.created} new contact(s). Total: ${r.total}.`);
      await refresh();
    } catch (e) {
      setSyncMsg(`Sync failed: ${e.message}`);
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMsg(null), 5000);
    }
  }

  async function refresh() {
    const [c, l] = await Promise.all([Admin.contacts.list(filters), Admin.lists.list()]);
    setContacts(c.contacts);
    setLists(l.lists);
  }
  useEffect(() => { refresh(); }, [filters.listId, filters.optIn, filters.source, filters.suppression]);

  function openCreate() { setForm(empty); setErr(null); setModal('create'); }
  function openEdit(c) {
    setForm({
      email: c.email, firstName: c.firstName || '', lastName: c.lastName || '',
      phone: c.phone || '', marketingOptIn: !!c.marketingOptIn, source: c.source,
      listIds: c.listIds || [], notes: c.notes || '',
    });
    setErr(null);
    setModal({ id: c.id, linked: !!c.userUid });
  }

  async function save(e) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      if (modal === 'create') await Admin.contacts.create(form);
      else await Admin.contacts.update(modal.id, form);
      setModal(null);
      await refresh();
    } catch (e) {
      setErr(e.data?.issues ? e.data.issues.map((i) => i.message).join(', ') : e.message);
    } finally { setBusy(false); }
  }

  async function remove(id) { await Admin.contacts.remove(id); await refresh(); }

  const toggleList = (id) => setForm((f) => ({
    ...f,
    listIds: f.listIds.includes(id) ? f.listIds.filter((x) => x !== id) : [...f.listIds, id],
  }));

  // Client-side suppression filter.
  const visible = contacts.filter((c) => {
    if (filters.suppression === 'active') return !c.suppressedAt;
    if (filters.suppression === 'suppressed') return !!c.suppressedAt;
    return true; // 'all'
  });
  const optedIn = visible.filter((c) => c.marketingOptIn).length;

  return (
    <div>
      <PageHeader
        eyebrow="Audience"
        title="Contacts"
        description="Everyone the club can email. Users, applicants, and manually-added contacts."
        action={
          <div className="flex gap-2">
            <button onClick={syncFromRecords} className="btn-outline btn-sm" disabled={syncing}>
              {syncing ? 'Syncing…' : 'Sync from records'}
            </button>
            <button onClick={openCreate} className="btn-primary btn-sm">+ New contact</button>
          </div>
        }
      />
      {syncMsg && (
        <div className="mb-4 px-3 py-2 rounded-md bg-success/10 text-success text-sm">{syncMsg}</div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select className="input max-w-xs" value={filters.listId} onChange={(e) => setFilters((f) => ({ ...f, listId: e.target.value }))}>
          <option value="">All lists</option>
          {lists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.contactCount})</option>)}
        </select>
        <select className="input max-w-[10rem]" value={filters.optIn} onChange={(e) => setFilters((f) => ({ ...f, optIn: e.target.value }))}>
          <option value="">Any opt-in</option>
          <option value="true">Opted in</option>
          <option value="false">Not opted in</option>
        </select>
        <select className="input max-w-[10rem]" value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}>
          <option value="">Any source</option>
          <option value="signup">Signup</option>
          <option value="join_request">Join request</option>
          <option value="clubhouse_inquiry">Clubhouse</option>
          <option value="manual">Manual</option>
          <option value="import">Import</option>
        </select>
        <select className="input max-w-[10rem]" value={filters.suppression} onChange={(e) => setFilters((f) => ({ ...f, suppression: e.target.value }))}>
          <option value="active">Active only</option>
          <option value="all">Show suppressed</option>
          <option value="suppressed">Suppressed only</option>
        </select>
        <span className="text-xs text-ink-3 ml-auto">
          {contacts.length} shown · {optedIn} opted in
        </span>
      </div>

      {visible.length === 0 ? (
        <EmptyState onAction={openCreate} label="Add a contact" />
      ) : (
        <div className="card-flush">
          <table className="hidden md:table w-full">
            <thead>
              <tr className="border-b border-line">
                <th className="t-head text-left px-5 py-3">Email</th>
                <th className="t-head text-left px-5 py-3">Name</th>
                <th className="t-head text-left px-5 py-3">Source</th>
                <th className="t-head text-left px-5 py-3">Lists</th>
                <th className="t-head text-left px-5 py-3">Opt-in</th>
                <th className="t-head text-right px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.id} className="border-b border-line last:border-0 hover:bg-line-soft/30">
                  <td className="px-5 py-3 text-sm font-medium">
                    {c.email}
                    {c.userUid && <span className="ml-2 text-[10px] text-ink-3">(member)</span>}
                  </td>
                  <td className="px-5 py-3 text-sm text-ink-2">{[c.firstName, c.lastName].filter(Boolean).join(' ') || '—'}</td>
                  <td className="px-5 py-3"><span className={sourceChip[c.source] || 'chip-neutral'}>{c.source}</span></td>
                  <td className="px-5 py-3 text-xs text-ink-2">
                    {(c.listIds || []).map((id) => lists.find((l) => l.id === id)?.name).filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-5 py-3">
                    {c.suppressedAt ? (
                      <SuppressionBadge c={c} />
                    ) : c.marketingOptIn ? (
                      <span className="chip-success chip-dot">yes</span>
                    ) : (
                      <span className="chip-neutral">no</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right space-x-1">
                    <button className="btn-outline btn-xs" onClick={() => openEdit(c)}>Edit</button>
                    <button className="btn-ghost btn-xs text-danger" onClick={() => setConfirm(c)}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="md:hidden divide-rule">
            {visible.map((c) => (
              <div key={c.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{c.email}</div>
                    <div className="text-xs text-ink-2 mt-0.5">{[c.firstName, c.lastName].filter(Boolean).join(' ') || '—'}</div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className={sourceChip[c.source] || 'chip-neutral'}>{c.source}</span>
                      {c.suppressedAt ? (
                        <SuppressionBadge c={c} />
                      ) : c.marketingOptIn ? (
                        <span className="chip-success chip-dot">opted in</span>
                      ) : (
                        <span className="chip-neutral">not opted in</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button className="btn-outline btn-xs" onClick={() => openEdit(c)}>Edit</button>
                    <button className="btn-ghost btn-xs text-danger" onClick={() => setConfirm(c)}>Del</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        eyebrow={modal === 'create' ? 'New' : modal?.linked ? 'Member contact' : 'Edit contact'}
        title={modal === 'create' ? 'Add contact' : form.email}
        size="lg"
        footer={
          <>
            <button type="button" onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
            <button type="button" onClick={() => submitForm('contact-form')} className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        {err && <div className="mb-4 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>}
        {modal?.linked && (
          <div className="mb-4 px-3 py-2 rounded-md bg-brand/5 border border-brand/15 text-xs text-ink-2">
            This contact is linked to a registered member. Email can't be changed from here.
          </div>
        )}
        <form id="contact-form" onSubmit={save} className="space-y-4">
          <FormField label="Email" required>
            <input type="email" className="input" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={modal?.linked}
              required autoFocus />
          </FormField>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="First name">
              <input className="input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            </FormField>
            <FormField label="Last name">
              <input className="input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            </FormField>
          </div>
          <FormField label="Phone">
            <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </FormField>
          <FormField label="Source">
            <select className="input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
              <option value="manual">Manual</option>
              <option value="signup">Signup</option>
              <option value="join_request">Join request</option>
              <option value="clubhouse_inquiry">Clubhouse inquiry</option>
              <option value="import">Import</option>
            </select>
          </FormField>
          <div>
            <div className="label">Lists</div>
            <div className="flex flex-wrap gap-2">
              {lists.map((l) => {
                const on = form.listIds.includes(l.id);
                return (
                  <button key={l.id} type="button" onClick={() => toggleList(l.id)}
                    className={`chip ${on ? 'bg-brand text-white' : 'bg-line-soft text-ink-2 hover:bg-line'}`}>
                    {l.name}
                  </button>
                );
              })}
              {!lists.length && <span className="text-xs text-ink-3">No lists yet.</span>}
            </div>
          </div>
          <FormField label="Notes">
            <textarea className="input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </FormField>
          <div className="pt-2 border-t border-line">
            <Switch
              checked={form.marketingOptIn}
              onChange={(v) => setForm({ ...form, marketingOptIn: v })}
              label="Marketing opt-in"
              hint="Only opted-in contacts receive campaigns"
            />
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => remove(confirm.id)}
        title={`Delete ${confirm?.email}?`}
        message={
          confirm?.userUid
            ? "This removes the contact record. The registered member account is untouched — they'll get re-added as a contact on their next profile save."
            : 'This removes the contact record entirely.'
        }
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}

const SUPPRESSION_LABEL = {
  bounce: 'Bounced',
  complaint: 'Complained',
  unsubscribe: 'Unsubscribed',
  manual: 'Suppressed',
  invalid_address: 'Invalid',
};
const SUPPRESSION_CHIP = {
  bounce: 'chip-danger',
  complaint: 'chip-danger',
  unsubscribe: 'chip-neutral',
  manual: 'chip-neutral',
  invalid_address: 'chip-danger',
};

function SuppressionBadge({ c }) {
  const reason = c.suppressionReason || 'manual';
  const label = SUPPRESSION_LABEL[reason] || 'Suppressed';
  const chip = SUPPRESSION_CHIP[reason] || 'chip-neutral';
  const when = c.suppressedAt ? new Date(c.suppressedAt).toLocaleDateString() : '';
  return (
    <span className={chip} title={`${label} on ${when}`}>
      {label}
    </span>
  );
}
